import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnChanges,
	SimpleChanges,
	inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import {
	GameColorPalette,
	SingleColor,
	ColorPurpose,
	GameCustomizationService,
} from '@meta-press/shared-plugins';

/**
 * Component for editing individual colors in a color palette
 * Allows users to modify colors using color picker and hex input
 */
@Component({
	selector: 'app-color-palette-editor',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './color-palette-editor.component.html',
	styleUrl: './color-palette-editor.component.scss',
})
export class ColorPaletteEditorComponent implements OnInit, OnChanges {
	@Input() palette!: GameColorPalette;
	@Input() readonly = false;
	@Input() mode: 'offcanvas' | 'inline' = 'offcanvas';
	@Input() hideHeader = false;

	/**
	 * Optional filter to show only specific ColorPurpose values
	 * If provided, only colors matching these purposes will be displayed
	 * If not provided, all colors in the palette will be shown
	 */
	@Input() visibleColors?: ColorPurpose[];

	/**
	 * Whether to show reset indicators for colors that differ from reference
	 * When true, shows an orange indicator on colors that have been modified
	 */
	@Input() showResetIndicators = false;

	/**
	 * Reference palette to compare against for showing reset indicators
	 * Colors that differ from this reference will show the reset indicator
	 */
	@Input() referencePalette?: GameColorPalette;

	/**
	 * Whether to hide the close button in inline mode
	 * Useful when the editor is always visible and cannot be closed
	 */
	@Input() hideCloseButton = false;

	@Output() paletteChange = new EventEmitter<GameColorPalette>();
	@Output() close = new EventEmitter<void>();
	/**
	 * Emitted when user clicks the reset indicator for a color
	 * The ColorPurpose indicates which color should be reset
	 */
	@Output() resetColor = new EventEmitter<ColorPurpose>();

	// Inject the active offcanvas reference (optional - only available when opened as offcanvas)
	public activeOffcanvas = inject(NgbActiveOffcanvas, { optional: true });

	// Inject GameCustomizationService for purpose definitions
	private gameCustomizationService = inject(GameCustomizationService);

	// Working copy of the palette for editing
	editablePalette!: GameColorPalette;

	// Primary color purposes (always visible)
	private primaryColorPurposes = [
		ColorPurpose.Primary,
		ColorPurpose.Secondary,
		ColorPurpose.Accent,
	];

	/**
	 * Generate color field definitions dynamically from the palette's colors Map
	 * Uses GameCustomizationService to get proper Finnish labels for each purpose
	 * If visibleColors is provided, only shows those purposes
	 */
	get colorFields() {
		if (!this.editablePalette?.colors) {
			return [];
		}

		const fields: Array<{
			purpose: ColorPurpose;
			label: string;
			required: boolean;
			isPrimary: boolean;
		}> = [];

		// Iterate through all colors in the palette
		for (const [purpose] of this.editablePalette.colors.entries()) {
			// Apply visibleColors filter if provided
			if (this.visibleColors && this.visibleColors.length > 0) {
				if (!this.visibleColors.includes(purpose)) {
					continue; // Skip this color if not in visibleColors
				}
			}

			const definition =
				this.gameCustomizationService.getColorPurposeDefinition(purpose);
			if (definition) {
				fields.push({
					purpose: purpose,
					label: definition.defaultName,
					required: true,
					isPrimary: this.primaryColorPurposes.includes(purpose),
				});
			}
		}

		return fields;
	}

	/**
	 * Get primary color fields for the always-visible section
	 */
	get primaryColors() {
		return this.colorFields.filter((f) => f.isPrimary);
	}

	/**
	 * Get other color fields for the collapsible section
	 */
	get otherColors() {
		return this.colorFields.filter((f) => !f.isPrimary);
	}

	/**
	 * Get count of hidden colors (colors in palette but not in visibleColors)
	 * Used to show "Ja X muuta väriä." message
	 */
	get hiddenColorsCount(): number {
		if (!this.visibleColors || this.visibleColors.length === 0) {
			return 0; // No filter applied, so no hidden colors
		}

		if (!this.editablePalette?.colors) {
			return 0;
		}

		// Count colors in palette that are not in visibleColors
		let hiddenCount = 0;
		for (const [purpose] of this.editablePalette.colors.entries()) {
			if (!this.visibleColors.includes(purpose)) {
				hiddenCount++;
			}
		}

		return hiddenCount;
	}

	ngOnInit(): void {
		// Initialize editable palette if palette is provided
		if (this.palette) {
			this.editablePalette = this.deepCopyPalette(this.palette);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['palette'] && this.palette) {
			// Create a deep copy of the palette for editing (including the colors Map)
			this.editablePalette = this.deepCopyPalette(this.palette);
		}
	}

	/**
	 * Create a deep copy of a palette including its colors Map
	 */
	private deepCopyPalette(palette: GameColorPalette): GameColorPalette {
		const colorsCopy = new Map<ColorPurpose, SingleColor>();
		for (const [purpose, color] of palette.colors.entries()) {
			colorsCopy.set(purpose, new SingleColor(color.color));
		}

		const copied = {
			id: palette.id,
			name: palette.name,
			description: palette.description,
			visibility: palette.visibility,
			pluginTypes: palette.pluginTypes,
			colors: colorsCopy,
		};

		// DEBUG: Verify the copy has proper Map
		console.log('[ColorPaletteEditor] deepCopyPalette output:', {
			id: copied.id,
			name: copied.name,
			colorsIsMap: copied.colors instanceof Map,
			colorsSize: copied.colors.size,
			colorsKeys: Array.from(copied.colors.keys())
		});

		return copied;
	}

	/**
	 * Handle color change from color picker or hex input
	 */
	onColorChange(purpose: ColorPurpose, newColor: string): void {
		if (this.readonly) return;

		// Validate hex color format
		if (!this.isValidHexColor(newColor)) {
			return;
		}

		// Update the color in the palette's colors Map
		this.editablePalette.colors.set(purpose, new SingleColor(newColor));

		// Emit the updated palette (create a new copy to trigger change detection)
		this.paletteChange.emit(this.deepCopyPalette(this.editablePalette));
	}

	/**
	 * Validate hex color format
	 */
	isValidHexColor(color: string): boolean {
		if (!color) return false;
		return /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color);
	}

	/**
	 * Get color value for a purpose from the palette's colors Map
	 */
	getColorValue(purpose: ColorPurpose): string {
		const colorObj = this.editablePalette?.colors?.get(purpose);

		// If it's a SingleColor object, extract the color value
		if (colorObj && colorObj.color) {
			return colorObj.color;
		}

		// Fallback for missing colors
		return '#ffffff';
	}

	/**
	 * Check if a color purpose has a valid value in the palette
	 */
	hasColorValue(purpose: ColorPurpose): boolean {
		const colorObj = this.editablePalette?.colors?.get(purpose);

		// Check if it's a SingleColor object with a valid color
		if (colorObj && colorObj.color) {
			return this.isValidHexColor(colorObj.color);
		}

		return false;
	}

	/**
	 * Handle hex input field changes
	 */
	onHexInputChange(purpose: ColorPurpose, event: Event): void {
		const input = event.target as HTMLInputElement;
		let value = input.value;

		// Add # if missing
		if (value && !value.startsWith('#')) {
			value = '#' + value;
			input.value = value;
		}

		this.onColorChange(purpose, value);
	}

	/**
	 * Handle color picker changes
	 */
	onColorPickerChange(purpose: ColorPurpose, event: Event): void {
		const input = event.target as HTMLInputElement;
		this.onColorChange(purpose, input.value);
	}

	/**
	 * Clear optional color field
	 */
	clearColorField(purpose: ColorPurpose): void {
		if (this.readonly) return;

		const fieldDef = this.colorFields.find((f) => f.purpose === purpose);
		if (fieldDef && !fieldDef.required) {
			this.editablePalette.colors.set(purpose, new SingleColor('#ffffff'));
			this.paletteChange.emit(this.deepCopyPalette(this.editablePalette));
		}
	}

	/**
	 * Trigger the color picker when the preview is clicked
	 */
	triggerColorPicker(event: Event): void {
		if (this.readonly) return;

		const colorPreview = event.target as HTMLElement;
		const colorPickerWrapper = colorPreview.parentElement;
		const colorInput = colorPickerWrapper?.querySelector(
			'.color-picker',
		) as HTMLInputElement;

		if (colorInput) {
			colorInput.click();
		}
	}

	/**
	 * Close the offcanvas panel
	 */
	closeOffcanvas(): void {
		if (this.activeOffcanvas) {
			this.activeOffcanvas.close();
		}
	}

	/**
	 * Close the inline editor
	 */
	closeInlineEditor(): void {
		this.close.emit();
	}

	/**
	 * Check if a color differs from the reference palette
	 * Used to determine whether to show the reset indicator
	 * @param purpose The color purpose to check
	 * @returns true if the color differs from the reference, false otherwise
	 */
	colorDiffersFromReference(purpose: ColorPurpose): boolean {
		if (!this.showResetIndicators || !this.referencePalette) {
			return false;
		}

		const currentColor = this.editablePalette?.colors?.get(purpose);
		const referenceColor = this.referencePalette?.colors?.get(purpose);

		// If either color is missing, they differ
		if (!currentColor || !referenceColor) {
			return currentColor !== referenceColor;
		}

		// Compare the color values (case-insensitive)
		return currentColor.color.toLowerCase() !== referenceColor.color.toLowerCase();
	}

	/**
	 * Handle click on the reset indicator
	 * Emits the resetColor event with the color purpose
	 * @param purpose The color purpose to reset
	 * @param event The click event (to stop propagation)
	 */
	onResetClick(purpose: ColorPurpose, event: Event): void {
		event.stopPropagation(); // Prevent triggering color picker
		this.resetColor.emit(purpose);
	}
}
